<template>
<Page noDrawer class="console" :logoLink="logoLink">
    <template #menu>
        <WorkspaceProjects v-if="workspace" :projects="workspace.projects" />

        <div v-if="!!projectId && $vuetify.breakpoint.mdAndUp" class="ml-2 flex-grow-1">
            <Search />
        </div>

        <v-spacer></v-spacer>

        <div class="ml-1">
            <TimePicker v-if="!!projectId" :small="$vuetify.breakpoint.xsOnly"/>
        </div>

        <v-btn :to="{name: 'workspace_settings'}" outlined plain height="40" class="ml-1 px-2">
            <v-icon>mdi-cog</v-icon>
        </v-btn>

        <div class="ml-1">
            <WorkspaceMenu :account="account" :workspace="workspace" />
        </div>

        <div class="ml-1">
            <ThemeSelector />
        </div>
    </template>

    <template v-if="workspace.subscription_status === 'trialing'" #alert>
        <v-system-bar v-if="workspace.trial && showTrialAlert" app height="40" :color="workspace.trial.status" dark class="pa-4">
            <div class="trial-alert">
                <template v-if="workspace.trial.status === 'warning'">
                    Just {{workspace.trial.left}} left in your trial period.
                    <template v-if="workspace.access === 'edit'">
                        <router-link :to="{name: 'workspace_settings', params: {tab: 'billing'}}">Activate</router-link> your subscription now.
                    </template>
                    <template v-else>
                        <router-link :to="{name: 'workspace_settings', params: {tab: 'team'}}">Contact</router-link> the workspace owner to activate your subscription.
                    </template>
                </template>
                <template v-else>
                    Hope you're enjoying your trial period! You've got {{workspace.trial.left}} remaining.
                    <template v-if="workspace.access === 'edit'">
                        Avoid last-minute hassles, <router-link :to="{name: 'workspace_settings', params: {tab: 'billing'}}">buy now</router-link>.
                    </template>
                </template>
            </div>
            <v-spacer />
            <v-btn @click="dismissTrialAlert()" icon><v-icon color="white">mdi-close</v-icon></v-btn>
        </v-system-bar>
    </template>

    <v-alert v-if="workspace.subscription_status === 'inactive' && $route.name !== 'workspace_settings'" color="red" elevation="2" border="left" class="mt-10" colored-border>
        <div class="d-block d-sm-flex align-center">
            <div class="flex-grow-1 mb-3 mb-sm-0">
                To continue using Coroot, please activate your subscription.
            </div>
            <v-btn outlined :to="{name: 'workspace_settings', params: {tab: 'billing'}}">Activate subscription</v-btn>
        </div>
    </v-alert>

    <v-card v-else-if="workspace.projects !== undefined && !(workspace.projects && workspace.projects.length) && $route.name !== 'workspace_settings'" class="mx-auto mt-16" max-width="800" flat>
        <div class="text-h5">Looks like you don't have any projects in the <b>{{workspace.name}}</b> workspace.</div>
        <div class="grey--text">Project is a separate infrastructure or environment with a dedicated Prometheus.</div>
        <v-btn block color="primary" class="mt-10" :to="{name: 'workspace_settings'}">Create a project</v-btn>
        <v-btn block outlined class="mt-3" :to="{name: 'workspaces'}">Switch workspaces</v-btn>
    </v-card>

    <router-view v-else />
</Page>
</template>

<script>
import Page from "@/pages/Page";
import WorkspaceProjects from "@/components/WorkspaceProjects";
import WorkspaceMenu from "@/components/WorkspaceMenu";
import Search from "@coroot/coroot/src/views/Search.vue";
import TimePicker from "@coroot/coroot/src/components/TimePicker.vue";
import ThemeSelector from "@coroot/coroot/src/components/ThemeSelector.vue";

const trialAlertKey = 'trial-alert-dismissed';

export default {
    props: {
        workspaceId: String,
        projectId: String,
    },

    components: {WorkspaceProjects, WorkspaceMenu, Search, Page, TimePicker, ThemeSelector},

    data() {
        return {
            account: {},
            workspace: {},
            showTrialAlert: false,
        };
    },

    computed: {
        logoLink() {
            if (this.projectId) {
                return {name: 'project', params: {workspaceId: this.workspaceId, projectId: this.projectId}};
            }
            return  {name: 'workspace', params: {workspaceId: this.workspaceId}};
        },
    },

    mounted() {
        this.get();
        this.$events.watch(this, this.get, 'refresh');
        this.showTrialAlert = (new Date().getTime() - localStorage.getItem(trialAlertKey)) > 24 * 60 * 60 * 1000;
    },

    beforeDestroy() {
        this.intercom('shutdown');
        this.$vuetify.theme.dark = false;
    },

    beforeRouteUpdate(to, from, next) {
        this.intercom('update');
        next();
    },

    watch: {
        $route: {
            handler: function () {
                this.redirect();
            },
            immediate: true,
        }
    },

    methods: {
        get() {
            this.$api.workspaceInfo(this.workspaceId, this.projectId, (data, meta) => {
                if (meta.code >= 400) {
                    this.$router.replace({name: 'workspaces'}).catch(err => err);
                    return;
                }
                this.workspace = data;
                this.$api.account((data, meta) => {
                    if (!meta.error) {
                        this.account = data || {};
                        this.intercom('boot');
                    }
                });
                this.redirect();
            });
        },
        redirect() {
            const w = this.workspace;
            if (this.$route.name === 'workspace' && w && w.projects && w.projects.length) {
                let id = this.$api.getLastProject();
                if (id) {
                    if (!w.projects.find((p) => p.id === id)) {
                        id = null;
                    }
                }
                if (!id) {
                    id = w.projects[0].id;
                }
                this.$router.replace({name: 'project', params: {projectId: id}}).catch(err => err);
            }
        },
        intercom(action) {
            if (!window.Intercom || this.isDemo || this.account.IsAdmin) {
                return;
            }
            if (action === 'shutdown') {
                window.Intercom(action);
                return;
            }
            const data = {
                "app_id": window.IntercomAppId,
                "email": this.account.Email,
                "company": {
                    "company_id": this.workspaceId,
                    "name": this.workspace.name,
                },
            };
            if (this.account.FirstName && this.account.LastName) {
                data['name'] = this.account.FirstName + ' ' + this.account.LastName;
            }
            if (this.account.TrialEndsAt) {
                data["trial_ends_at"] = this.account.TrialEndsAt;
            }
            window.Intercom(action, data);
        },
        dismissTrialAlert() {
            this.showTrialAlert = false;
            localStorage.setItem(trialAlertKey, new Date().getTime().toString());
        },
    },
};
</script>

<style scoped>
.trial-alert {
    font-size: 1rem;
    color: white;
}
.trial-alert a {
    color: white;
    font-weight: 700;
}
</style>
